import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import headerImg from '../assets/header.png';
import Footer from '../components/Footer';

const LandingPage = () => {

  // Upgraded Intersection Observer for cinematic focus-pull reveals
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px 0px -10% 0px',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('opacity-0', 'translate-y-16', 'scale-[0.92]', 'blur-md');
          entry.target.classList.add('opacity-100', 'translate-y-0', 'scale-100', 'blur-0');
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    const revealElements = document.querySelectorAll('.reveal-element');
    revealElements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // Inline styles for ultra-premium cinematic animations
  const premiumStyles = `
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@200;300;400;500;600;700;800&display=swap');
    
    .font-premium {
      font-family: 'Outfit', sans-serif;
    }

    /* Ambient floating lighting */
    @keyframes blob {
      0% { transform: translate(0px, 0px) scale(1); }
      33% { transform: translate(30px, -50px) scale(1.1); }
      66% { transform: translate(-20px, 20px) scale(0.9); }
      100% { transform: translate(0px, 0px) scale(1); }
    }
    .animate-blob {
      animation: blob 10s infinite cubic-bezier(0.4, 0, 0.2, 1);
    }
    .animation-delay-2000 { animation-delay: 2s; }
    .animation-delay-4000 { animation-delay: 4s; }

    /* Parallax slow pan */
    @keyframes slowPan {
      0% { transform: scale(1.05) translateY(0); }
      50% { transform: scale(1.15) translateY(-2%); }
      100% { transform: scale(1.05) translateY(0); }
    }
    .bg-cinematic-pan {
      animation: slowPan 30s ease-in-out infinite;
    }

    /* Hyper-realistic liquid glass */
    .liquid-glass {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.01) 100%);
      backdrop-filter: blur(24px) saturate(180%);
      -webkit-backdrop-filter: blur(24px) saturate(180%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-top: 1px solid rgba(255, 255, 255, 0.2);
      border-left: 1px solid rgba(255, 255, 255, 0.15);
      box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1);
    }
    
    .liquid-glass-light {
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.7) 0%, rgba(255, 255, 255, 0.3) 100%);
      backdrop-filter: blur(30px) saturate(200%);
      -webkit-backdrop-filter: blur(30px) saturate(200%);
      border: 1px solid rgba(255, 255, 255, 0.4);
      box-shadow: 0 40px 80px rgba(0, 0, 0, 0.08), inset 0 2px 20px rgba(255, 255, 255, 0.5);
    }

    /* Realistic 3D water bubbles */
    @keyframes rise {
      0% { transform: translateY(0) scale(0.8) rotate(0deg); opacity: 0; }
      20% { opacity: 0.8; }
      80% { opacity: 0.5; }
      100% { transform: translateY(-120vh) scale(1.5) rotate(360deg); opacity: 0; }
    }
    .bubble {
      position: absolute;
      bottom: -150px;
      background: radial-gradient(150% 150% at 30% 30%, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.1) 40%, rgba(255, 255, 255, 0) 50%);
      border: 1px solid rgba(255,255,255,0.4);
      border-radius: 50%;
      animation: rise infinite cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: inset 0 0 15px rgba(255,255,255,0.4), 0 0 20px rgba(255,255,255,0.1);
      backdrop-filter: blur(3px);
    }
    
    /* Chrome/Liquid text gradient */
    .text-liquid-gradient {
      background: linear-gradient(to right, #ffffff 20%, #a5f3fc 40%, #ffffff 60%, #e0f2fe 80%);
      background-size: 200% auto;
      color: #000;
      background-clip: text;
      text-fill-color: transparent;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      animation: shine 8s linear infinite;
    }
    
    @keyframes shine {
      to { background-position: 200% center; }
    }
    
    /* Button shimmer */
    .btn-shimmer {
      position: relative;
      overflow: hidden;
    }
    .btn-shimmer::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 50%;
      height: 100%;
      background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent);
      transform: skewX(-20deg);
      animation: shimmer 4s infinite 2s;
    }
    @keyframes shimmer {
      100% { left: 200%; }
    }
  `;

  return (
    <div className="font-premium bg-[#020617] text-on-surface selection:bg-cyan-500/30 selection:text-cyan-50">
      <style>{premiumStyles}</style>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/918138993705"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-[#25D366] text-white p-4 rounded-full shadow-[0_10px_40px_rgba(37,211,102,0.5)] hover:scale-110 hover:-translate-y-3 transition-all duration-500 z-50 flex items-center justify-center group overflow-hidden border border-white/20 backdrop-blur-sm"
        aria-label="Contact Tank Care on WhatsApp"
      >
        <div className="absolute inset-0 bg-white/20 group-hover:scale-150 transition-transform duration-500 rounded-full opacity-0 group-hover:opacity-100"></div>
        <svg className="w-8 h-8 fill-current relative z-10" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
        </svg>
      </a>

      {/* Hero Section */}
      <section className="relative min-h-[100vh] flex items-center justify-center overflow-hidden pt-20 bg-[#020617]">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-cover bg-center bg-cinematic-pan opacity-80" style={{ backgroundImage: `url(${headerImg})` }}></div>
          {/* Multi-layered cinematic vignette & overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#020617]/90 via-[#083344]/50 to-[#020617] mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#020617_100%)] opacity-80"></div>
        </div>

        {/* Ambient lighting blobs */}
        <div className="absolute top-0 -left-40 w-96 h-96 bg-cyan-600/30 rounded-full mix-blend-screen filter blur-[100px] opacity-50 animate-blob"></div>
        <div className="absolute top-40 -right-40 w-96 h-96 bg-blue-600/30 rounded-full mix-blend-screen filter blur-[100px] opacity-50 animate-blob animation-delay-2000"></div>

        {/* Cinematic Water Bubbles */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          {[...Array(18)].map((_, i) => (
            <div 
              key={i} 
              className="bubble" 
              style={{
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 60 + 10}px`,
                height: `${Math.random() * 60 + 10}px`,
                animationDuration: `${Math.random() * 12 + 8}s`,
                animationDelay: `${Math.random() * 5}s`
              }}
            ></div>
          ))}
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col items-center text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            <div className="liquid-glass px-6 py-3 rounded-full flex items-center gap-3 animate-fade-in-up hover:bg-white/5 transition-colors duration-500" style={{ animationDelay: "100ms" }}>
              <span className="material-symbols-outlined text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" style={{ fontVariationSettings: "'FILL' 1" }}>verified_user</span>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-cyan-50">Tank Care Calicut</span>
            </div>
            <div className="liquid-glass px-6 py-3 rounded-full flex items-center gap-3 animate-fade-in-up hover:bg-white/5 transition-colors duration-500" style={{ animationDelay: "200ms" }}>
              <span className="material-symbols-outlined text-cyan-400 drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]" style={{ fontVariationSettings: "'FILL' 1" }}>eco</span>
              <span className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-cyan-50">100% Eco-Friendly</span>
            </div>
          </div>
          
          <h1 className="text-6xl md:text-8xl lg:text-[10rem] font-extrabold text-white mb-6 max-w-6xl tracking-tighter leading-[1] animate-fade-in-up drop-shadow-2xl" style={{ animationDelay: "300ms" }}>
            <span className="text-liquid-gradient">Purity</span> in <br className="md:hidden" />Every Drop.
          </h1>
          
          <p className="text-xl md:text-2xl text-cyan-50/70 mb-12 max-w-3xl mx-auto font-light leading-relaxed animate-fade-in-up" style={{ animationDelay: "400ms" }}>
            Experience the intersection of clinical hygiene and premium service. <strong>Tank Care</strong> is a premier Calicut-based agency ensuring your water storage is flawlessly clean, structurally sound, and perfectly safe.
          </p>
          
          <Link to="/booking" className="btn-shimmer group liquid-glass text-white px-10 py-5 rounded-full font-semibold text-lg hover:bg-cyan-500 hover:border-cyan-400 hover:-translate-y-2 transition-all duration-500 shadow-[0_10px_40px_rgba(34,211,238,0.15)] hover:shadow-[0_20px_60px_rgba(34,211,238,0.4)] animate-fade-in-up flex items-center gap-4" style={{ animationDelay: "500ms" }}>
            Book Premium Clean
            <span className="material-symbols-outlined group-hover:translate-x-2 transition-transform duration-500">arrow_forward</span>
          </Link>
        </div>
        
      </section>

      {/* How It Works (Scrollytelling) */}
      <section className="py-40 bg-[#f8fafc] relative overflow-hidden" id="how-it-works">
        {/* Soft volumetric lighting for light section */}
        <div className="absolute top-[-20%] right-[-10%] w-[80%] h-[80%] rounded-full bg-cyan-100/50 mix-blend-multiply filter blur-[120px] pointer-events-none animate-blob"></div>
        <div className="absolute bottom-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full bg-blue-100/50 mix-blend-multiply filter blur-[120px] pointer-events-none animate-blob animation-delay-4000"></div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-24 reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md transition-all duration-[1.2s] ease-[cubic-bezier(0.25,1,0.5,1)]">
            <h2 className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-6 tracking-tighter">Precision Process.</h2>
            <p className="text-2xl text-slate-500 max-w-3xl mx-auto font-light leading-relaxed">Three steps to absolute water purity for your premises, delivered with minimal disruption by Calicut's finest.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {/* Step 1 */}
            <div className="liquid-glass-light p-12 rounded-[2.5rem] flex flex-col items-start relative group hover:-translate-y-4 hover:shadow-[0_40px_80px_rgba(8,145,178,0.12)] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-cyan-50 to-white flex items-center justify-center mb-10 group-hover:from-cyan-500 group-hover:to-blue-500 transition-all duration-700 shadow-[0_10px_30px_rgba(0,0,0,0.05)] group-hover:shadow-[0_20px_40px_rgba(8,145,178,0.3)]">
                <span className="material-symbols-outlined text-4xl text-cyan-600 group-hover:text-white transition-colors duration-700" style={{ fontVariationSettings: "'FILL' 1" }}>calendar_month</span>
              </div>
              <div className="absolute top-10 right-10 text-8xl font-black text-slate-100 select-none group-hover:text-cyan-50 transition-colors duration-700">1</div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">Schedule</h3>
              <p className="text-lg text-slate-500 leading-relaxed font-light">Select a convenient time through our streamlined booking platform. Tank Care will seamlessly schedule a day to reach your location in Calicut.</p>
            </div>
            {/* Step 2 */}
            <div className="liquid-glass-light p-12 rounded-[2.5rem] flex flex-col items-start relative group hover:-translate-y-4 hover:shadow-[0_40px_80px_rgba(8,145,178,0.12)] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] delay-[150ms] reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-cyan-50 to-white flex items-center justify-center mb-10 group-hover:from-cyan-500 group-hover:to-blue-500 transition-all duration-700 shadow-[0_10px_30px_rgba(0,0,0,0.05)] group-hover:shadow-[0_20px_40px_rgba(8,145,178,0.3)]">
                <span className="material-symbols-outlined text-4xl text-cyan-600 group-hover:text-white transition-colors duration-700" style={{ fontVariationSettings: "'FILL' 1" }}>water_drop</span>
              </div>
              <div className="absolute top-10 right-10 text-8xl font-black text-slate-100 select-none group-hover:text-cyan-50 transition-colors duration-700">2</div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">Execute</h3>
              <p className="text-lg text-slate-500 leading-relaxed font-light">Our certified local technicians perform a comprehensive drain, multi-stage sanitization, and structural integrity inspection on-site.</p>
            </div>
            {/* Step 3 */}
            <div className="liquid-glass-light p-12 rounded-[2.5rem] flex flex-col items-start relative group hover:-translate-y-4 hover:shadow-[0_40px_80px_rgba(8,145,178,0.12)] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] delay-[300ms] reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md">
              <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-cyan-50 to-white flex items-center justify-center mb-10 group-hover:from-cyan-500 group-hover:to-blue-500 transition-all duration-700 shadow-[0_10px_30px_rgba(0,0,0,0.05)] group-hover:shadow-[0_20px_40px_rgba(8,145,178,0.3)]">
                <span className="material-symbols-outlined text-4xl text-cyan-600 group-hover:text-white transition-colors duration-700" style={{ fontVariationSettings: "'FILL' 1" }}>science</span>
              </div>
              <div className="absolute top-10 right-10 text-8xl font-black text-slate-100 select-none group-hover:text-cyan-50 transition-colors duration-700">3</div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">Verify</h3>
              <p className="text-lg text-slate-500 leading-relaxed font-light">Post-clean water testing ensures optimal pH and zero contaminants. You receive a detailed digital health report of your reserve.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-40 bg-white relative overflow-hidden" id="services">
        {/* Subtle mesh grid background overlay for tech/premium feel */}
        <div className="absolute inset-0 z-0 opacity-[0.03] pointer-events-none" style={{ backgroundImage: 'linear-gradient(#000 1px, transparent 1px), linear-gradient(90deg, #000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-24 reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md transition-all duration-[1.2s] ease-[cubic-bezier(0.25,1,0.5,1)]">
            <h2 className="text-5xl md:text-7xl font-extrabold text-slate-900 mb-6 tracking-tighter">Premium Solutions.</h2>
            <p className="text-2xl text-slate-500 max-w-3xl mx-auto font-light leading-relaxed">Advanced aquatic hygiene solutions for every scale across Calicut, ensuring pristine water quality and safety.</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {/* Service 1 */}
            <div className="p-12 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:border-cyan-200 hover:bg-white hover:shadow-[0_40px_100px_rgba(8,145,178,0.08)] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md">
              <div className="w-16 h-16 rounded-full bg-cyan-50 flex items-center justify-center text-cyan-600 mb-8 group-hover:scale-110 group-hover:bg-cyan-100 transition-all duration-500">
                <span className="material-symbols-outlined text-3xl">home</span>
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">Residential Cleansing</h3>
              <p className="text-lg text-slate-500 leading-relaxed font-light flex-grow">Deep vacuuming and scrubbing for domestic overhead and underground water tanks, tailored for households.</p>
            </div>
            {/* Service 2 */}
            <div className="p-12 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:border-cyan-200 hover:bg-white hover:shadow-[0_40px_100px_rgba(8,145,178,0.08)] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group delay-[150ms] reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md">
              <div className="w-16 h-16 rounded-full bg-cyan-50 flex items-center justify-center text-cyan-600 mb-8 group-hover:scale-110 group-hover:bg-cyan-100 transition-all duration-500">
                <span className="material-symbols-outlined text-3xl">domain</span>
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">Commercial Reserves</h3>
              <p className="text-lg text-slate-500 leading-relaxed font-light flex-grow">High-capacity rapid cleaning for residential complexes, malls, and corporate buildings in the Calicut region.</p>
            </div>
            {/* Service 3 */}
            <div className="p-12 rounded-[2.5rem] bg-slate-50 border border-slate-100 hover:border-cyan-200 hover:bg-white hover:shadow-[0_40px_100px_rgba(8,145,178,0.08)] transition-all duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] group delay-[300ms] reveal-element opacity-0 translate-y-16 scale-[0.92] blur-md">
              <div className="w-16 h-16 rounded-full bg-cyan-50 flex items-center justify-center text-cyan-600 mb-8 group-hover:scale-110 group-hover:bg-cyan-100 transition-all duration-500">
                <span className="material-symbols-outlined text-3xl">healing</span>
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight">Medical-Grade Sanitization</h3>
              <p className="text-lg text-slate-500 leading-relaxed font-light flex-grow">UV-C light treatment and strictly safe chemical washes tailored for hospital and clinic water reserves.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LandingPage;